/**
 * Timezone suggestions for the settings screen.
 *
 * These duplicate three helpers from `packages/core/src/domain/timezones.ts`, deliberately.
 * The web app does not import `@planner/core` — doing so would pull Drizzle and the Postgres
 * driver into the browser bundle — and asking the API for a list the browser already holds
 * would be a round trip for `Intl.supportedValuesOf`, which is right here.
 *
 * What is *not* duplicated is the rule. Only the server decides whether a zone is
 * acceptable, and that asymmetry is on purpose: if this runtime's `Intl` and Node's ever
 * disagree about an obscure zone, a client-side check would fail closed and refuse something
 * the server would happily store. Suggesting is safe; judging is not.
 */

/** Every zone this browser knows. Falls back to a short list rather than throwing. */
export function listTimezones(): string[] {
  try {
    return [...Intl.supportedValuesOf('timeZone')];
  } catch {
    return [...FALLBACK_ZONES];
  }
}

const FALLBACK_ZONES = [
  'UTC',
  'Europe/London',
  'Europe/Warsaw',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Tokyo',
  'Australia/Sydney',
] as const;

/**
 * The zone this browser is in, or null when it will not say.
 *
 * Null rather than a guess: a plausible-looking wrong zone is the kind of thing nobody
 * checks, so it is offered as a suggestion and never applied on its own.
 */
export function detectTimezone(): string | null {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * "Europe/Warsaw — GMT+2, 21:45".
 *
 * The offset and the local clock are what make a zone recognisable. The name alone asks the
 * user to know which side of an administrative border they are on.
 */
export function describeTimezone(zone: string, now: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'shortOffset',
    }).formatToParts(now);

    const offset = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
    const minute = parts.find((p) => p.type === 'minute')?.value ?? '';
    return `${zone} — ${offset}, ${hour}:${minute}`;
  } catch {
    // A zone the list offered but this runtime cannot format: show the bare name rather
    // than dropping it, since the server may still accept it.
    return zone;
  }
}

/**
 * The calendar day an instant falls on, in the given zone.
 *
 * The same answer `localDay` gives in `packages/core/src/domain/periods.ts`, under the same name,
 * for the same reason: a stored instant is UTC and the day it belongs to is a question about a
 * zone (INV-UTC). `en-CA` is chosen because these strings are compared and sorted as strings, and
 * it is the locale that yields `YYYY-MM-DD`.
 *
 * The browser needs it wherever it turns a `dueAt` back into a day — `dueLook` did that with the
 * *browser's* zone, so a deadline at 23:30 in the account's evening read as tomorrow's, or
 * yesterday's, depending on which side of the account the laptop was sitting.
 */
export const localDay = (instant: Date, timezone: string): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);

/**
 * Today, in the *user's* zone.
 *
 * Two copies of this existed and only one was right. `Today.tsx` took the timezone as an argument
 * because `new Date().toISOString()` gives the UTC date, which showed yesterday to anyone east of
 * Greenwich after local 22:00. `TaskOverview` then wrote its own from `getFullYear`/`getMonth`/
 * `getDate` — the *browser's* zone, which is a third answer again, and not the one the server
 * counted with.
 *
 * The visible failure: the "Planned today" tile showed a count and opened an empty list, for as
 * many hours as the browser's zone differs from the profile's.
 */
export const todayInZone = (timezone: string): string => localDay(new Date(), timezone);

/**
 * The day before or after, by whole days.
 *
 * Done on the date string rather than with `Date` arithmetic. Local `setDate` crosses DST at the
 * wrong moment and produced I-24 once already — the bug that put yesterday's work on today's row.
 * Noon anchors the instant far from either boundary.
 *
 * Lives here rather than in `Today.tsx`, which is where it was written, because `todayInZone` is
 * the only correct thing to hand it: any caller doing whole-day arithmetic is asking about the
 * account's calendar, and the two answers belong in one place. `Habits.tsx`'s Pause is the second
 * caller — it was doing the same arithmetic on `Date.now()`, which is the UTC day.
 */
export const shiftDay = (day: string, by: number): string => {
  const at = new Date(`${day}T12:00:00Z`);
  at.setUTCDate(at.getUTCDate() + by);
  return at.toISOString().slice(0, 10);
};

/**
 * The last day of the week `day` falls in — Sunday, counting weeks from Monday.
 *
 * The "This week" filter's other half. It used to be `d.setDate(d.getDate() + (7 - ((d.getDay() +
 * 6) % 7) - 1))` on a browser `Date`, so it counted from the browser's date and could land a day
 * out whenever the laptop and the account were on different calendars. Same arithmetic, on the
 * account's day string, next to `shiftDay` for the reason that one gives: any caller doing
 * whole-day arithmetic is asking about the account's calendar.
 *
 * Monday-based because that is the week the control has always meant, not because it is the only
 * one — `habits.week_starts_on` exists and this does not read it. Offering the filter a week
 * start is a product decision and nobody has asked for it; mirroring `weekdayOf` in
 * `packages/core/src/domain/periods.ts` keeps the two spellings of "which day of the week is
 * this" the same answer in the meantime.
 */
export const endOfWeek = (day: string): string =>
  shiftDay(day, 6 - ((new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7));

/**
 * The Monday the week `day` falls in — the other end of `endOfWeek`.
 *
 * Same arithmetic on the same account day string, and Monday-based for the same reason: that is
 * the week every control in this app has meant since the "This week" filter was written, and
 * `weekdayOf` in `packages/core/src/domain/periods.ts` spells it the same way. The week canvas
 * sends this to `/api/week/:weekStart`, which snaps whatever it is given back to a Monday anyway
 * — so the two cannot disagree about which week is on screen even if this line is wrong, and the
 * server's answer carries the `weekStart` it actually used.
 */
export const startOfWeek = (day: string): string =>
  shiftDay(day, -((new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7));
